import React, { useEffect, useState, useRef } from 'react';
import { useIsMobileLayout } from '@/hooks';
import Draggable from 'react-draggable';
import { createPortal } from 'react-dom';
import { getViewerClip } from '@/helpers/viewerClip';
import { useDispatch, useSelector } from 'react-redux';
import { updatePositionParticipant } from '@/redux/slices/document';
import Cookies from 'js-cookie';
import axios from 'axios';

const useIsMobile = () => {
  const isMobile = useIsMobileLayout();
  return isMobile;
};

const DraggableLineOfSignature = ({ participant, page, position, scaleX, scaleY, lineWidth, lineHeight, lineExtra, borderSize, fontSizeLabel, handleStop, isMobile }) => {
  const nodeRef = useRef(null);

  // Posición controlada en px para Draggable
  const [localPxPos, setLocalPxPos] = useState({ x: position.x * scaleX, y: position.y * scaleY });

  // Sincronizar cuando Redux actualiza la posición (después de drop) o cambia la escala
  const prevPosRef = useRef({ x: position.x, y: position.y });
  useEffect(() => {
    if (prevPosRef.current.x !== position.x || prevPosRef.current.y !== position.y) {
      prevPosRef.current = { x: position.x, y: position.y };
      setLocalPxPos({ x: position.x * scaleX, y: position.y * scaleY });
    }
  }, [position.x, position.y, scaleX, scaleY]);

  // Refs para evitar stale closures en el touch handler
  const localPxPosRef = useRef(localPxPos);
  localPxPosRef.current = localPxPos;
  const scaleXRef = useRef(scaleX);
  scaleXRef.current = scaleX;
  const scaleYRef = useRef(scaleY);
  scaleYRef.current = scaleY;
  const participantRef = useRef(participant);
  participantRef.current = participant;
  const handleStopRef = useRef(handleStop);
  handleStopRef.current = handleStop;

  // Touch drag manual para mobile
  const touchStartRef = useRef(null);
  useEffect(() => {
    if (!isMobile || !nodeRef.current) return;
    const el = nodeRef.current;

    const onTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      touchStartRef.current = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        startPx: { ...localPxPosRef.current }
      };
    };

    const onTouchMove = (e) => {
      if (!touchStartRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const dx = touch.clientX - touchStartRef.current.clientX;
      const dy = touch.clientY - touchStartRef.current.clientY;
      const newPx = {
        x: touchStartRef.current.startPx.x + dx,
        y: touchStartRef.current.startPx.y + dy
      };
      el.style.transform = `translate(${newPx.x}px, ${newPx.y}px)`;
      el._pendingPx = newPx;
    };

    const onTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      e.preventDefault();
      touchStartRef.current = null;
      if (!el._pendingPx) return;
      const { x: px, y: py } = el._pendingPx;
      el._pendingPx = null;
      const origX = px / scaleXRef.current;
      const origY = py / scaleYRef.current;
      setLocalPxPos({ x: px, y: py });
      handleStopRef.current(participantRef.current, { x: origX, y: origY });
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile]);

  return (
    <Draggable
      nodeRef={nodeRef}
      key={`line-${participant.email}-${page}`}
      bounds="parent"
      position={localPxPos}
      disabled={isMobile}
      onDrag={(e, data) => {
        if (!isMobile) setLocalPxPos({ x: data.x, y: data.y });
      }}
      onStop={(e, data) => {
        if (isMobile) return;
        const origX = data.x / scaleX;
        const origY = data.y / scaleY;
        handleStop(participant, { x: origX, y: origY });
      }}
    >
      <div ref={nodeRef} style={{ position: 'absolute', zIndex: isMobile ? 10 : 2, cursor: 'move', touchAction: isMobile ? 'none' : 'auto', width: lineWidth, height: lineHeight + lineExtra, borderBottom: `${borderSize}px solid black`, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
        <p style={{ margin: 0, fontSize: fontSizeLabel, width: '100%', whiteSpace: 'nowrap', overflow: 'visible', textOverflow: 'clip', textAlign: 'center', position: 'relative', top: `${lineExtra}px` }}>
          {`${participant.name} ${participant.last_name || ''}`}
        </p>
      </div>
    </Draggable>
  );
};

// Línea de firma en mobile. Igual que las cajas de selección (DocumentPdf), dentro de la hoja un iPhone no la
// pintaba en varias hojas (la 1, o todas al agregarla a todas las hojas) aunque sí estaba en el DOM. Se dibuja en
// una capa fija (portal a <body>) que sigue a la hoja en pantalla cada cuadro y se recorta al visor. El arrastre
// convierte píxeles de pantalla a píxeles de hoja con el factor real (incluye el zoom) y guarda al soltar.
const MobileFixedLine = ({ participant, page, position, scaleX, scaleY, lineWidth, lineHeight, lineExtra, borderSize, fontSizeLabel, handleStop, pageWidth, pageHeight }) => {
  const [frame, setFrame] = useState(null);
  const [dragPx, setDragPx] = useState(null); // posición en píxeles de hoja mientras se arrastra / hasta que llegue la de redux
  const dragRef = useRef(null);

  useEffect(() => {
    let raf;
    const same = (a, b) => a && b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 &&
      Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;
    const tick = () => {
      const el = document.querySelector(`[data-page-container="true"][data-page-index="${page}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        const rect = { left: r.left, top: r.top, width: r.width, height: r.height };
        const clip = getViewerClip(el);
        setFrame((prev) => (
          prev && same(prev.rect, rect) && prev.clip && clip && prev.clip.left === clip.left &&
          prev.clip.top === clip.top && prev.clip.right === clip.right && prev.clip.bottom === clip.bottom
            ? prev
            : { rect, clip }
        ));
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [page]);

  // Cuando redux ya trae la posición nueva, se deja de usar la local.
  useEffect(() => { setDragPx(null); }, [position.x, position.y]);

  if (!frame || !frame.clip || !pageWidth || frame.rect.width === 0) return null;

  const zf = frame.rect.width / pageWidth; // zoom visual de la hoja
  const elHeight = lineHeight + lineExtra;
  const px = dragPx || { x: position.x * scaleX, y: position.y * scaleY };
  const left = frame.rect.left + px.x * zf;
  const top = frame.rect.top + px.y * zf;
  const width = lineWidth * zf;
  const height = elHeight * zf;
  const { clip } = frame;
  // El nombre se dibuja debajo de la línea, fuera de la caja del elemento: el recorte al visor usa márgenes
  // negativos (inset) para NO recortar la caja propia, solo lo que se sale del visor.
  const labelRoom = lineExtra * zf * 3; // espacio bajo la línea que ocupa el nombre
  const clipPath = `inset(${clip.top - top}px ${left + width - clip.right}px ${top + height - clip.bottom}px ${clip.left - left}px)`;
  const hidden = top >= clip.bottom || top + height + labelRoom <= clip.top || left >= clip.right || left + width <= clip.left;

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { cx: e.clientX, cy: e.clientY, start: { ...px } };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    // bounds="parent": la línea no sale de la hoja
    const x = Math.min(Math.max(0, d.start.x + (e.clientX - d.cx) / zf), Math.max(0, pageWidth - lineWidth));
    const y = Math.min(Math.max(0, d.start.y + (e.clientY - d.cy) / zf), Math.max(0, pageHeight - elHeight));
    setDragPx({ x, y });
  };
  const onPointerEnd = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (err) { /* ya liberado */ }
    }
    if (!d || !dragPx) return;
    handleStop(participant, { x: dragPx.x / scaleX, y: dragPx.y / scaleY });
  };
  const stop = (e) => e.stopPropagation();

  if (hidden) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        clipPath,
        // La hoja tiene line-height: 0 (DocumentPdf) y la etiqueta de la línea se acomoda con eso: debajo de la
        // línea. Fuera de la hoja heredaba el del <body> y el texto quedaba encima de la línea.
        lineHeight: 0,
        zIndex: 8800,
        touchAction: 'none',
        borderBottom: `${borderSize}px solid black`,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        boxSizing: 'content-box'
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onTouchStart={stop}
      onTouchEnd={stop}
      onClick={stop}
    >
      <p style={{ margin: 0, fontSize: `${parseFloat(fontSizeLabel) * zf}px`, width: '100%', whiteSpace: 'nowrap', overflow: 'visible', textAlign: 'center', position: 'relative', top: `${lineExtra * zf}px`, pointerEvents: 'none' }}>
        {`${participant.name} ${participant.last_name || ''}`}
      </p>
    </div>,
    document.body
  );
};

const SignPos = ({ page }) => {
  const dispatch = useDispatch();
  const { participants, positions, documentInfo, dimenssions } = useSelector(({ document }) => document);
  const userInfo = useSelector(({ userInfo }) => userInfo);
  const isMobile = useIsMobile();
  const yOffset = -60;
  const scaleX = dimenssions.width / documentInfo.width;
  const scaleY = dimenssions.height / documentInfo.height;

  const savePositionsinDB = async (participant, documentId, page, x, y) => {
    const yi = y + yOffset;
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}signs/savePositions`, {
        signerId: participant.email === userInfo.email ? userInfo.id : participant.id,
        documentId,
        page,
        x,
        y: yi,
        isOwner: participant.email === userInfo.email
      },
      {
        withCredentials: true
      });

      if (response.data.ok) {
        dispatch(updatePositionParticipant({
          participant,
          page,
          x,
          y,
          id: response.data.id,
          type: 'lineOfSignature',
          signed: false
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const deletePositionInDB = async (id, signerId, documentId, isOwner) => {
    console.log('parametros en delateposition:', id, signerId, documentId, isOwner);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BASE_URL}signs/deletePosition`, {
        id,
        signerId,
        documentId,
        isOwner
      },
      {
        withCredentials: true
      });
      if (response.data) {
        console.log('Eliminando posición en DB', response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleStop = async (participant, { x, y }) => {
    console.log(`Position of ${participant.name}'s signature line moved to x: ${x}, y: ${y} on page ${page}`);

    const position = positions[page]?.find(pos => pos.email === participant.email && pos.type === 'lineOfSignature');

    // Actualizar Redux PRIMERO, antes de cualquier await, para que "Confirmar" lea la posición correcta
    dispatch(updatePositionParticipant({
      participant,
      page,
      x,
      y,
      type: 'lineOfSignature'
    }));

    if (position && position.id) {
      await deletePositionInDB(position.id, participant.id, documentInfo.id, participant.email === userInfo.email);
    }

    await savePositionsinDB(participant, documentInfo.id, page, x, y);
  };

  // lineHeightBase + lineExtraBase = elementHeightBase en unidades PDF (constante, independiente del zoom)
  // Mobile: 10 + 10 = 20 PDF units | Desktop: 16 + 15 = 31 PDF units
  const lineHeightBase = isMobile ? 10 : 16;
  const lineExtraBase = isMobile ? 10 : 15;
  const lineWidth = isMobile ? 90 : 180 * scaleX;
  const lineHeight = lineHeightBase * scaleY;   // píxeles pantalla (proporcional)
  const lineExtra = lineExtraBase * scaleY;      // píxeles pantalla (proporcional)
  const borderSize = isMobile ? 1 : 2;
  const fontSizeLabel = isMobile ? `${Math.max(7, Math.round(9 * scaleY))}px` : '11px';

  return (
    participants.map(participant => {
      const position = positions[page]?.find(pos => pos.email === participant.email && pos.type === 'lineOfSignature');
      if (position && scaleX > 0 && scaleY > 0 && isMobile) {
        return (
          <MobileFixedLine
            key={`${participant.email}-${page}`}
            participant={participant}
            page={page}
            position={position}
            scaleX={scaleX}
            scaleY={scaleY}
            lineWidth={lineWidth}
            lineHeight={lineHeight}
            lineExtra={lineExtra}
            borderSize={borderSize}
            fontSizeLabel={fontSizeLabel}
            handleStop={handleStop}
            pageWidth={dimenssions.width}
            pageHeight={dimenssions.height}
          />
        );
      }
      return position && scaleX > 0 && scaleY > 0 && (
        <DraggableLineOfSignature
          key={`${participant.email}-${page}`} // Added unique key
          participant={participant}
          page={page}
          position={position}
          scaleX={scaleX}
          scaleY={scaleY}
          lineWidth={lineWidth}
          lineHeight={lineHeight}
          lineExtra={lineExtra}
          borderSize={borderSize}
          fontSizeLabel={fontSizeLabel}
          handleStop={handleStop}
          isMobile={isMobile}
        />
      );
    })
  );
};

export default SignPos;
